<template>
  <v-container>
    <h2 class="my-3">
      Globale Variablen
      <v-tooltip
        v-if="loaded && changed"
        right
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            x-small
            class="ml-2"
            v-bind="attrs"
            v-on="on"
            @click="$emit('reset')"
          >
            <v-icon>fa-undo</v-icon>
          </v-btn>
        </template>
        <span>auf Ausgangswerte zurücksetzen</span>
      </v-tooltip>
    </h2>
    <h4>Geschätzer Anteil E-Fahrzeuge / Jahr</h4>
    <v-alert
      v-if="showDefaultValueAlert"
      type="info"
      border="top"
      colored-border
      elevation="2"
      class="mt-3"
    >
      Die hier vorgeschlagenen Werte sind angelehnt an Annahmen der EL80/95-Klimazielszenarien der 2018 erschienenen
      <a
        target="_blank"
        href="https://www.dena.de/newsroom/publikationsdetailansicht/pub/dena-leitstudie-integrierte-energiewende/"
      >dena-Leitstudie Integrierte Energiewende</a>,
      die nach aktuellem Kenntnissstand nach oben korrigiert wurden:
      <ul>
        <li>linearer Zuwachs der Neuzulassungsrate von E-PKW auf 33% bis 2030</li>
        <li>10 Jahre PKW-Marktverweildauer</li>
        <li>gleichbleibende Gesamtflottenstärke von 44,7 Mio PKW</li>
      </ul>
    </v-alert>
    <v-row>
      <v-col cols="auto">
        <v-chart
          class="chart"
          :option="chartOption"
        />
      </v-col>
      <v-col>
        <v-data-table
          :key="tableUpdateKey"
          :headers="globalCivilizationTableHeaders"
          :items="globalCivilizations"
          :items-per-page="-1"
          :loading="!loaded"
          :dense="globalCivilizations.length > 7"
          hide-default-footer
          disable-sort
        >
          <template v-slot:body="{ items }">
            <tbody>
              <template v-for="(item, index) in items">
                <!-- highlight table rows based on edit state -->
                <tr
                  :key="item.globalCivilizationKey"
                  :class="
                    item.editing
                      ? 'cyan lighten-5'
                      : item.new
                        ? 'light-green lighten-4'
                        : item.changed
                          ? 'yellow lighten-4'
                          : null
                  "
                >
                  <td>{{ item.year }}</td>
                  <td class="eCarFractionTd">
                    <EditSettingTextField
                      v-model="globalCivilizations"
                      :original-item-array="globalCivilizationsOriginal"
                      :index="index"
                      :rules="editECarFractionRules"
                      id-field="globalCivilizationKey"
                      edit-field="eCarFraction"
                      :mode="mode"
                      @update="tableUpdateKey++"
                    />
                  </td>
                  <td>
                    <v-btn
                      v-if="mode == 'edit'"
                      icon
                      @click="globalCivilizations.splice(index, 1)"
                    >
                      <v-icon>fa-trash</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </tbody>
          </template>
          <template
            v-if="mode == 'edit'"
            v-slot:footer
          >
            <v-container>
              <v-text-field
                v-model="addYear"
                :rules="addYearRules"
                label="Jahr hinzufügen"
                style="max-width: 300px"
                @keydown.enter="
                  if (addYearRulesValid) addYearToGlobalCivilizations();
                "
                @update:error="addYearErrorHandler"
              >
                <template v-slot:append-outer>
                  <v-btn
                    icon
                    :disabled="!addYearRulesValid"
                    color="primary"
                    @click="addYearToGlobalCivilizations"
                  >
                    <v-icon>fa-plus</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-container>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EditSettingTextField from "@/components/dialogs/updateSettingsDialog/EditSettingTextField";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import {
  TitleComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent
} from "echarts/components";
import VChart from "vue-echarts";
use([
  GridComponent,
  CanvasRenderer,
  TitleComponent,
  BarChart,
  DatasetComponent,
  TransformComponent
]);
export default {
  name: "EditGlobalVariables",
  components: { VChart, EditSettingTextField },
  model: {
    prop: "globalCivilizations",
    event: "change"
  },
  props: {
    globalCivilizations: Array,
    globalCivilizationsOriginal: Array,
    loaded: Boolean,
    changed: Boolean,
    mode: String,
    showDefaultValueAlert: Boolean
  },
  data() {
    return {
      tableUpdateKey: 0, // used to re-render table
      globalCivilizationTableHeaders: [
        { text: "Jahr", value: "year" },
        { text: "Anteil E-Fahrzeuge", value: "eCarFraction", sortable: false },
        { value: "globalCivilizationKey", sortable: false }
      ],
      currentYear: String(new Date().getFullYear()),
      addYear: null,
      addYearRulesValid: false
    };
  },
  computed: {
    // chart config
    chartOption() {
      return {
        dimensions: ["year", "eCarFraction"],
        dataset: [
          {
            source: this.globalCivilizations
          },
          {
            transform: {
              type: "sort",
              config: { dimension: "year", order: "asc" }
            }
          }
        ],
        series: { 
          "itemStyle": {
            color: "#4caf50"
          },
          type: "bar",
          dataSetIndex: 1,
          encode: { x: "year", y: "eCarFraction" }
        },
        xAxis: { type: "category" },
        yAxis: {}
      };
    },
    addYearRules() {
      return [
        input =>
          !input ||
          /^\d\d\d\d$/.test(input) ||
          "Jahr muss im Format yyyy angegeben werden",
        input =>
          !input ||
          input > this.currentYear ||
          "Jahr muss in der Zukunft liegen",
        input =>
          !input ||
          !this.globalCivilizations
            .map(elem => elem.year.toString())
            .includes(input) ||
          "Jahr bereits in Tabelle"
      ];
    },
    editECarFractionRules() {
      return [
        input =>
          /^0(\.\d+)?$/.test(input) ||
          /^1(\.0+)?$/.test(input) ||
          "Anteil muss im Format 0.xxx+ angegeben werden"
      ];
    }
  },
  methods: {
    addYearErrorHandler(error) {
      this.addYearRulesValid = Boolean(this.addYear) && !error;
    },
    // insert new year into globalCivilizations at correct index (as sorted by year)
    addYearToGlobalCivilizations() {
      let newYear = parseInt(this.addYear);
      let i = this.globalCivilizations.findIndex(
        elem => elem.year > parseInt(newYear)
      );
      this.globalCivilizations.splice(
        i != -1 ? i : this.globalCivilizations.length,
        0,
        {
          year: newYear,
          eCarFraction: "0",
          globalCivilizationKey:
            // choose max key + 1 for now
            // TODO: keys have to be reset on PUT/POST
            Math.max(
              ...this.globalCivilizations.map(
                elem => elem.globalCivilizationKey
              )
            ) + 1,
          new: true
        }
      );
      this.addYear = null;
      this.addYearRulesValid = false;
    }
  }
};
</script>

<style scoped>
.chart {
  height: 400px;
  width: 700px;
}
.eCarFractionTd {
  width: 200px;
}
</style>
